import { ChangeDetectionStrategy, Component, inject, model, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UsersStoreService } from '../../../store/users-store.service';
import { UsersDetailComponent } from '../../components/users-detail/users-detail.component';
import { UserDetailSkeletonComponent } from '../../components/user-detail-skeleton/user-detail-skeleton.component';
import { ClientInfoEndorsementFormComponent } from '@webapp/endorsements/ui/components/client-info-endorsement-form/client-info-endorsement-form.component';
import { SidebarComponent } from '@webapp/ui';
import { IUser, UserRole, UserStatusValue } from '../../../interfaces/users.interface';
import { AgentReassignFormComponent } from '../../components/agent-reassign-form/agent-reassign-form.component';

@Component({
  selector: 'app-users-detail-page',
  imports: [
    UsersDetailComponent,
    UserDetailSkeletonComponent,
    ClientInfoEndorsementFormComponent,
    SidebarComponent,
    AgentReassignFormComponent,
  ],
  templateUrl: './users-detail-page.component.html',
  styleUrl: './users-detail-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsersDetailPageComponent implements OnInit {
  store = inject(UsersStoreService);

  user = this.store.get();
  loading = this.store.getLoading();

  route = inject(ActivatedRoute);
  
  currentUserId = '';
  visible = model(false);
  agentSidebarVisible = model(false);

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.currentUserId = params['id'];
      this.store.detailStore.fetch(params['id']);
    });
  }

  openUserEditModal() {
    this.visible.set(true);
  }

  exitSidebar() {
    this.visible.set(false);
    this.store.detailStore.fetch(this.currentUserId);
  }

  openAgentReassign() {
    this.agentSidebarVisible.set(true);
  }

  // Close BEFORE refetching: fetch() flips `loading`, which unmounts this whole block
  // (sidebar included). Same order as exitSidebar().
  exitAgentSidebar() {
    this.agentSidebarVisible.set(false);
    this.store.detailStore.fetch(this.currentUserId);
  }

  closeAgentSidebar() {
    this.agentSidebarVisible.set(false);
  }

  // Convert IUserDetail to IUser for the client-info-endorsement-form
  getUserForEndorsementForm(): IUser | undefined {
    const currentUser = this.user();
    if (!currentUser) return undefined;
    
    return {
      ...currentUser,
      role: this.mapStringToUserRole(currentUser.role as string)
    };
  }

  private mapStringToUserRole(role: string): UserRole | undefined {
    const roleMap: Record<string, UserRole> = {
      'CLIENT': UserRole.CLIENT,
      'AGENT': UserRole.AGENT,
      'BROKER': UserRole.BROKER,
      'ADMIN': UserRole.ADMIN,
      'PROSPECT': UserRole.PROSPECT,
      'INSPECTOR': UserRole.INSPECTOR,
    };
    return roleMap[role];
  }

  refreshUserData = () => {
    const currentUserId = this.route.snapshot.params['id'];
    if (currentUserId) {
      this.store.detailStore.fetch(currentUserId);
    }
  };

  async changeUserStatus(status: UserStatusValue) {
    await this.store.changeUserStatus(this.currentUserId, status);
  }
}
